import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CloudinaryUploadResponse {
  asset_id: string;
  public_id: string;
  version: number;
  version_id: string;
  signature: string;
  width: number;
  height: number;
  format: string;
  resource_type: string;
  created_at: string;
  tags: string[];
  bytes: number;
  type: string;
  etag: string;
  placeholder: boolean;
  url: string;
  secure_url: string;
  folder: string;
  original_filename: string;
  original_extension: string;
}

@Injectable({
  providedIn: 'root'
})
export class CloudinaryUploadService {
  private http = inject(HttpClient);

  /**
   * Uploads a file to Cloudinary using unsigned upload preset
   * @param file The file to upload
   * @param cloudName The Cloudinary cloud name (defaults to environment config)
   * @param uploadPreset The unsigned upload preset (defaults to environment config)
   */
  uploadFile(file: File, cloudName?: string, uploadPreset?: string): Observable<CloudinaryUploadResponse> {
    const cName = cloudName || environment.cloudinaryCloudName;
    const uPreset = uploadPreset || environment.cloudinaryUploadPreset;

    if (!cName || !uPreset || cName === 'your-cloud-name') {
      throw new Error('Cloudinary configuration is missing. Please configure cloudinaryCloudName and cloudinaryUploadPreset in environment.ts');
    }

    // "auto" resource type automatically handles images, videos, and raw files (pdfs, etc.)
    const url = `https://api.cloudinary.com/v1_1/${cName}/auto/upload`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uPreset);

    return this.http.post<CloudinaryUploadResponse>(url, formData);
  }

  /**
   * Deletes a file from Cloudinary. 
   * Note: Unsigned deletes are generally not supported for security reasons via the frontend upload API alone,
   * but if you have a backend proxy or an authenticated token, this could be configured here.
   * By default, with unsigned uploads, deleting must be done via backend admin API.
   * This method is just a placeholder to keep compatibility with existing Azure component logic.
   */
  deleteFile(publicId: string): Observable<any> {
    console.warn('Frontend unsigned deletion is typically not allowed by Cloudinary. Recommend implementing a backend endpoint or using a signed request.');
    // In a real scenario, call your backend: return this.http.delete(`/api/cloudinary/destroy/${publicId}`);
    return new Observable(subscriber => {
      subscriber.next(true);
      subscriber.complete();
    });
  }
}
